/**
 * 把逾時當下的原料（fetch 階段 + 網路狀態）結論成一個「可能原因」。
 *
 * 為什麼需要：逾時事件裡本來就有 inflightStage / onLine / httpStatus，但沒有一個欄位直接回答
 * 「最可能是什麼問題」，每個排查者都得自己套一次決策樹。這裡把決策樹寫成程式，結果同時放進
 * Sentry 的 error name、fingerprint 與 tag，讓 issue 列表一眼就分得出網路、後端或前端自身。
 *
 * 限制：前端無法單靠瀏覽器 API 在 SERVER_NO_RESPONSE 內再切出「網路 vs 後端」，
 * 那條線要靠結局事件帶回的 httpStatus / trace header 對到後端 log。
 */

export const TIMEOUT_CAUSE = {
    /** 裝置離線（navigator.onLine === false） */
    OFFLINE: 'offline',
    /** fetch() 尚未被呼叫，卡在 optionsGetter / access token；不是這支 API 本身的問題 */
    BLOCKED_BEFORE_REQUEST: 'blocked-before-request',
    /** 請求已送出、response headers 未回；網路異常或後端異常皆可能 */
    SERVER_NO_RESPONSE: 'server-no-response',
    /** headers 已回、body 仍在下載；後端已回應但回應大或傳輸慢 */
    SLOW_RESPONSE_BODY: 'slow-response-body',
    /** API 早已完成但 timer 嚴重延遲；主執行緒被卡 */
    MAIN_THREAD_BLOCKED: 'main-thread-blocked',
    /** 防禦性 fallback */
    UNKNOWN: 'unknown'
};

// buildFetchInstrumentedTiming() 的 inflightStage → 原因；順序無關，離線判定在此之前
const STAGE_TO_CAUSE = {
    'pre-request-dispatch': TIMEOUT_CAUSE.BLOCKED_BEFORE_REQUEST,
    'waiting-server': TIMEOUT_CAUSE.SERVER_NO_RESPONSE,
    downloading: TIMEOUT_CAUSE.SLOW_RESPONSE_BODY,
    'completed-callback-late': TIMEOUT_CAUSE.MAIN_THREAD_BLOCKED
};

/**
 * 純函式，無副作用，任何輸入都不會 throw。
 *
 * 判定順序：
 *   1. 離線 → OFFLINE（優先於任何階段：斷網時卡在哪一階段都沒有診斷意義）
 *   2. 依 inflightStage 對照 STAGE_TO_CAUSE
 *   3. 其餘 → UNKNOWN
 *
 * @param {object} [input]
 * @param {{ inflightStage?: string }} [input.fetchInstrumented] - buildFetchInstrumentedTiming() 的回傳值
 * @param {{ navigatorAvailable?: boolean, onLine?: boolean }} [input.networkInfo] - getNetworkInfo() 的回傳值
 * @returns {string} TIMEOUT_CAUSE 的成員之一
 */
export function classifyTimeoutCause({ fetchInstrumented, networkInfo } = {}) {
    if (networkInfo?.navigatorAvailable && networkInfo.onLine === false) {
        return TIMEOUT_CAUSE.OFFLINE;
    }

    const stage = fetchInstrumented?.inflightStage;
    if (typeof stage === 'string' && Object.prototype.hasOwnProperty.call(STAGE_TO_CAUSE, stage)) {
        return STAGE_TO_CAUSE[stage];
    }

    return TIMEOUT_CAUSE.UNKNOWN;
}
